import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const AiSuggestions: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>AI-Assist (Local LLM) — Sprout Docs</title>
        <meta
          name="description"
          content="How Sprout's AI-assist works: a local large language model (Llama-3.1-8B via Ollama) running on the same server as the API. Observation text never leaves the operator's infrastructure — POPIA-aligned by design."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/curriculum/ai-suggestions" />
      </Helmet>

      <h1>AI-Assist</h1>
      <p className="lead">
        Sprout has a small, optional AI helper that can polish a teacher's
        rough observation notes and suggest which CAPS Learning Outcome an
        observation best evidences. It runs on a local model on the same
        server as the API — observation text never leaves the operator's
        infrastructure.
      </p>

      <h2>What it does</h2>
      <ul>
        <li>
          <strong>Polish a draft</strong> — turns rough teacher notes into
          a clearer narrative without changing what was actually observed.
          The teacher sees both versions and accepts or rejects the
          suggestion.
        </li>
        <li>
          <strong>Suggest a CAPS outcome (discovery mode)</strong> — when
          a teacher hasn't tagged an outcome yet, the model proposes up to
          two candidates with a brief rationale and a confidence pill
          (high / medium / low). The teacher approves before saving.
        </li>
        <li>
          <strong>Check outcome alignment (targeted mode)</strong> — if an
          observation is already tagged to an outcome, the model checks
          whether the narrative actually evidences that outcome. If not,
          it suggests behaviour the teacher could note that would.
        </li>
      </ul>

      <h2>Why local, not OpenAI / Anthropic / Google</h2>
      <p>
        Children's observation text is personal information under POPIA.
        Sending it to a third-party AI processor would mean a cross-border
        transfer to that processor, an additional data-processing
        agreement, and a meaningful privacy disclosure to guardians. The
        local-LLM approach sidesteps all three: the model runs on the same
        machine as the Sprout API, reads the observation, returns a
        suggestion, and the data never touches an external service.
      </p>
      <p>
        It also means the feature works on schools with flaky internet —
        as long as the API itself is reachable, the AI is.
      </p>

      <h2>How it's wired</h2>
      <ul>
        <li>
          <strong>Model:</strong> Llama-3.1-8B, served via{' '}
          <a href="https://ollama.com" target="_blank" rel="noreferrer">
            Ollama
          </a>{' '}
          on the API host.
        </li>
        <li>
          <strong>Endpoint:</strong> <code>POST /api/v1/observations/suggest</code> —
          accepts the draft narrative and (optionally) a learning outcome
          ID, returns a list of issues, a suggested rewrite, and (in
          discovery mode) candidate outcomes with confidence.
        </li>
        <li>
          <strong>Failure mode:</strong> if Ollama is unreachable or the
          model times out, the endpoint returns an empty suggestion. The
          editor still works; the AI just goes quiet. Saves never depend
          on the AI being available.
        </li>
      </ul>

      <h2>What the AI cannot do</h2>
      <ul>
        <li>It cannot save observations, tag outcomes, or change child records on its own — every suggestion needs teacher approval.</li>
        <li>It cannot invent CAPS codes — in discovery mode, it picks from a candidate list of real outcomes loaded from the tenant DB. Hallucinated codes are filtered out before they reach the UI.</li>
        <li>It is not a substitute for the teacher's judgement. The confidence pill exists precisely because the model is occasionally wrong — teachers should ignore low-confidence suggestions.</li>
      </ul>

      <h2>Turning it off</h2>
      <p>
        Operators who don't want AI in the loop can disable the
        observation-suggest endpoint at the API layer (feature flag,
        config). With the endpoint disabled, the editor's "AI Suggest"
        button is hidden and observations work entirely manually.
      </p>

      <p>
        Related: <Link to="/docs/security/popia">POPIA Compliance</Link>{' '}
        covers the broader privacy framework, and{' '}
        <Link to="/docs/security/data-residency">Data Residency</Link>{' '}
        explains where customer data physically lives.
      </p>
    </>
  );
};

export default AiSuggestions;
